import { db } from "../firebase";
import {
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	where,
	orderBy,
	limit,
	serverTimestamp,
	onSnapshot,
	setDoc,
	updateDoc,
	Timestamp,
} from "firebase/firestore";

/**
 * BioFlow Sensor Service
 * Handles all Firestore operations for BioFlow water meter sensor data
 */

// Collection names
const BIOFLOW_COLLECTION = "BioFlow";
const USER_SENSORS_COLLECTION = "UserSiloSensors";
const DATA_SUBCOLLECTION = "Data";

const WATER_SENSOR_ALLOWED_METADATA = [
	"location",
	"installationDate",
	"serialNumber",
];

/**
 * Parse timestamp from document ID format: "2025-09-15_13-37-55Z"
 */
const parseDocIdTimestamp = (docId) => {
	if (!docId || typeof docId !== "string") return null;

	const isoMatch = docId.match(
		/^(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})Z$/
	);

	if (!isoMatch) return null;

	const [, datePart, hours, minutes, seconds] = isoMatch;
	const isoString = `${datePart}T${hours}:${minutes}:${seconds}Z`;
	const parsed = new Date(isoString);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Normalize timestamp from various formats
 */
const normalizeTimestamp = (value, docId = null) => {
	if (!value) {
		return parseDocIdTimestamp(docId);
	}

	if (value instanceof Date) return value;
	if (typeof value.toDate === "function") return value.toDate();
	if (typeof value === "number") {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	if (typeof value === "object" && value !== null) {
		if (typeof value.seconds === "number") {
			return new Date(value.seconds * 1000);
		}
	}
	if (typeof value === "string") {
		const parsed = new Date(value);
		if (!Number.isNaN(parsed.getTime())) {
			return parsed;
		}
	}

	return parseDocIdTimestamp(docId);
};

/**
 * Get all BioFlow sensors for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of sensor objects
 */
export const getUserBioFlowSensors = async (userId, group = null) => {
	if (!userId) {
		return [];
	}

	try {
		// Get user registrations from UserSiloSensors
		const queryConstraints = [
			where("userId", "==", userId),
			where("type", "==", "water"),
			where("isActive", "==", true)
		];
		
		if (group !== null) {
			queryConstraints.push(where("group", "==", group));
		}
		
		const userSensorsQuery = query(
			collection(db, USER_SENSORS_COLLECTION),
			...queryConstraints
		);

		const userSensorsSnapshot = await getDocs(userSensorsQuery);
		const sensors = [];

		for (const docSnapshot of userSensorsSnapshot.docs) {
			const sensorData = docSnapshot.data();
			const deviceId = sensorData.deviceId;

			// Get device document from BioFlow collection
			const deviceRef = doc(db, BIOFLOW_COLLECTION, deviceId);
			const deviceDoc = await getDoc(deviceRef);

			// Get latest data reading
			let latestData = null;
			try {
				const dataRef = collection(
					db,
					BIOFLOW_COLLECTION,
					deviceId,
					DATA_SUBCOLLECTION
				);
				const dataQuery = query(
					dataRef,
					orderBy("timestamp", "desc"),
					limit(1)
				);
				const dataSnapshot = await getDocs(dataQuery);
				if (!dataSnapshot.empty) {
					const latestDoc = dataSnapshot.docs[0];
					const latestDocData = latestDoc.data();
					latestData = {
						...latestDocData,
						timestamp: normalizeTimestamp(
							latestDocData.timestamp,
							latestDoc.id
						),
					};
				}
			} catch (error) {
				console.error(
					`[bioFlowSensorService][getUserBioFlowSensors] Error fetching latest data for ${deviceId}:`,
					error
				);
			}

			if (deviceDoc.exists()) {
				const deviceData = deviceDoc.data();
				sensors.push({
					...sensorData,
					...deviceData,
					id: docSnapshot.id,
					type: "water",
					group: sensorData.group || null,
					latestData,
					// Map device fields
					firmwareVersion: deviceData.firmwareVersion,
					ipAddress: deviceData.ipAddress,
					sensorType: deviceData.sensorType,
					status: deviceData.status,
					updatedAt: normalizeTimestamp(deviceData.updatedAt),
					lastUpdated: normalizeTimestamp(
						deviceData.lastUpdated || deviceData.updatedAt
					),
					// Include latest data fields if available
					rssi: latestData?.rssi ?? deviceData.rssi,
					ssid: latestData?.ssid ?? deviceData.ssid,
				});
			} else {
				// Device document doesn't exist, but user has registration
				sensors.push({
					...sensorData,
					id: docSnapshot.id,
					type: "water",
					group: sensorData.group || null,
					latestData,
					lastUpdated: null,
				});
			}
		}

		return sensors;
	} catch (error) {
		console.error(
			"[bioFlowSensorService][getUserBioFlowSensors] Error getting user BioFlow sensors:",
			error
		);
		throw error;
	}
};

/**
 * Get a single BioFlow sensor device document
 * @param {string} deviceId - Device ID (MAC address)
 * @returns {Promise<Object|null>} Sensor device object or null
 */
export const getBioFlowSensor = async (deviceId) => {
	if (!deviceId) {
		return null;
	}

	try {
		const deviceRef = doc(db, BIOFLOW_COLLECTION, deviceId);
		const deviceDoc = await getDoc(deviceRef);

		if (!deviceDoc.exists()) {
			return null;
		}

		const data = deviceDoc.data();
		return {
			id: deviceDoc.id,
			...data,
			updatedAt: normalizeTimestamp(data.updatedAt),
			lastUpdated: normalizeTimestamp(data.lastUpdated || data.updatedAt),
		};
	} catch (error) {
		console.error(
			`[bioFlowSensorService][getBioFlowSensor] Error getting sensor ${deviceId}:`,
			error
		);
		throw error;
	}
};

/**
 * Register a BioFlow sensor to a user
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID (MAC address)
 * @param {string} sensorName - User-friendly name for the sensor
 * @param {Object} metadata - Additional metadata (location, installationDate, serialNumber)
 * @returns {Promise<void>}
 */
export const registerBioFlowSensorToUser = async (
	userId,
	deviceId,
	sensorName,
	metadata = {},
	group = null
) => {
	if (!userId || !deviceId) {
		throw new Error("User ID and Device ID are required");
	}

	try {
		// Add to UserSiloSensors collection for user-to-sensor mapping
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);

		const preparedMetadata = { ...metadata };
		if (preparedMetadata.installationDate instanceof Date) {
			preparedMetadata.installationDate = Timestamp.fromDate(
				preparedMetadata.installationDate
			);
		}

		await setDoc(
			userSensorRef,
			{
				userId,
				deviceId,
				sensorName,
				type: "water",
				registeredAt: serverTimestamp(),
				isActive: true,
				group: group || null,
				...preparedMetadata,
			},
			{ merge: true }
		);

		// Store ownership information inside BioFlow document
		const sensorRef = doc(db, BIOFLOW_COLLECTION, deviceId);

		const sanitizedMetadata = WATER_SENSOR_ALLOWED_METADATA.reduce(
			(acc, key) => {
				if (metadata[key]) {
					acc[key] = metadata[key];
				}
				return acc;
			},
			{}
		);

		if (sanitizedMetadata.installationDate instanceof Date) {
			sanitizedMetadata.installationDate = Timestamp.fromDate(
				sanitizedMetadata.installationDate
			);
		}

		await setDoc(
			sensorRef,
			{
				ownerId: userId,
				sensorName,
				type: "water",
				deviceId,
				registeredAt: serverTimestamp(),
				...sanitizedMetadata,
			},
			{ merge: true }
		);
	} catch (error) {
		console.error(
			`[bioFlowSensorService][registerBioFlowSensorToUser] Error registering sensor:`,
			error
		);
		throw error;
	}
};

/**
 * Remove a BioFlow sensor from a user
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @returns {Promise<void>}
 */
export const removeBioFlowSensorFromUser = async (userId, deviceId) => {
	if (!userId || !deviceId) {
		throw new Error("User ID and Device ID are required");
	}

	try {
		// Mark as inactive in UserSiloSensors
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		await setDoc(
			userSensorRef,
			{
				isActive: false,
				removedAt: serverTimestamp(),
			},
			{ merge: true }
		);

		// Remove user ownership from BioFlow document
		const sensorRef = doc(db, BIOFLOW_COLLECTION, deviceId);
		await setDoc(
			sensorRef,
			{
				ownerId: null,
				removedAt: serverTimestamp(),
			},
			{ merge: true }
		);
	} catch (error) {
		console.error(
			`[bioFlowSensorService][removeBioFlowSensorFromUser] Error removing sensor:`,
			error
		);
		throw error;
	}
};

/**
 * Get latest BioFlow sensor data readings
 * @param {string} deviceId - Device ID
 * @param {number} limitCount - Number of recent readings to fetch
 * @returns {Promise<Array>} Array of sensor data
 */
export const getBioFlowSensorData = async (deviceId, limitCount = 50) => {
	if (!deviceId) {
		return [];
	}

	try {
		const dataRef = collection(
			db,
			BIOFLOW_COLLECTION,
			deviceId,
			DATA_SUBCOLLECTION
		);
		const q = query(dataRef, orderBy("timestamp", "desc"), limit(limitCount));

		const querySnapshot = await getDocs(q);
		const data = [];

		querySnapshot.forEach((docSnapshot) => {
			const docData = docSnapshot.data();
			data.push({
				id: docSnapshot.id,
				...docData,
				timestamp: normalizeTimestamp(docData.timestamp, docSnapshot.id),
			});
		});

		return data;
	} catch (error) {
		console.error(
			`[bioFlowSensorService][getBioFlowSensorData] Error getting sensor data:`,
			error
		);
		throw error;
	}
};

/**
 * Subscribe to real-time BioFlow sensor data updates
 * @param {string} deviceId - Device ID
 * @param {function} callback - Callback function for updates
 * @returns {function} Unsubscribe function
 */
export const subscribeBioFlowSensorData = (deviceId, callback) => {
	if (!deviceId) {
		return () => {};
	}

	const dataRef = collection(
		db,
		BIOFLOW_COLLECTION,
		deviceId,
		DATA_SUBCOLLECTION
	);
	const q = query(dataRef, orderBy("timestamp", "desc"), limit(1));

	return onSnapshot(
		q,
		(querySnapshot) => {
			if (!querySnapshot.empty) {
				const latestDoc = querySnapshot.docs[0];
				const latestData = latestDoc.data();
				callback({
					id: latestDoc.id,
					...latestData,
					timestamp: normalizeTimestamp(
						latestData.timestamp,
						latestDoc.id
					),
				});
			} else {
				callback(null);
			}
		},
		(error) => {
			console.error(
				`[bioFlowSensorService][subscribeBioFlowSensorData] Error in sensor data subscription:`,
				error
			);
		}
	);
};

/**
 * Get BioFlow sensor chart data for visualization
 * @param {string} deviceId - Device ID
 * @param {Date} startDate - Start date for data range
 * @param {Date} endDate - End date for data range
 * @returns {Promise<Array>} Array of sensor data for charting
 */
export const getBioFlowSensorChartData = async (
	deviceId,
	startDate,
	endDate
) => {
	if (!deviceId) {
		return [];
	}

	try {
		const dataRef = collection(
			db,
			BIOFLOW_COLLECTION,
			deviceId,
			DATA_SUBCOLLECTION
		);

		// Try the optimized query first
		try {
			const q = query(
				dataRef,
				where("timestamp", ">=", Timestamp.fromDate(startDate)),
				where("timestamp", "<=", Timestamp.fromDate(endDate)),
				orderBy("timestamp", "asc")
			);

			const querySnapshot = await getDocs(q);
			const data = [];

			querySnapshot.forEach((docSnapshot) => {
				const docData = docSnapshot.data();
				const timestamp = normalizeTimestamp(
					docData.timestamp,
					docSnapshot.id
				);

				if (!timestamp) return;

				data.push({
					timestamp,
					flowRate: Number(docData.flowRate ?? 0),
					totalVolume: Number(docData.totalVolume ?? 0),
					pulseCount: Number(docData.pulseCount ?? 0),
					millis: Number(docData.millis ?? 0),
					rssi: docData.rssi ?? null,
					ssid: docData.ssid ?? null,
				});
			});

			return data;
		} catch (queryError) {
			// If the query fails (likely due to missing index), try a simpler approach
			console.warn(
				`[bioFlowSensorService][getBioFlowSensorChartData] Composite query failed, using fallback:`,
				queryError
			);

			const fallbackQuery = query(
				dataRef,
				orderBy("timestamp", "desc"),
				limit(1000)
			);
			const querySnapshot = await getDocs(fallbackQuery);
			const data = [];

			const startTime = startDate.getTime();
			const endTime = endDate.getTime();

			querySnapshot.forEach((docSnapshot) => {
				const docData = docSnapshot.data();
				const timestamp = normalizeTimestamp(
					docData.timestamp,
					docSnapshot.id
				);

				if (!timestamp) return;

				if (
					timestamp.getTime() >= startTime &&
					timestamp.getTime() <= endTime
				) {
					data.push({
						timestamp,
						flowRate: Number(docData.flowRate ?? 0),
						totalVolume: Number(docData.totalVolume ?? 0),
						pulseCount: Number(docData.pulseCount ?? 0),
						millis: Number(docData.millis ?? 0),
						rssi: docData.rssi ?? null,
						ssid: docData.ssid ?? null,
					});
				}
			});

			data.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
			return data;
		}
	} catch (error) {
		console.error(
			`[bioFlowSensorService][getBioFlowSensorChartData] Error getting sensor chart data:`,
			error
		);
		throw error;
	}
};

/**
 * Get BioFlow sensor settings from user's sensor document
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} Sensor settings
 */
export const getBioFlowSensorSettings = async (userId, deviceId) => {
	if (!userId || !deviceId) {
		return null;
	}

	try {
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		const settingsDoc = await getDoc(userSensorRef);

		if (settingsDoc.exists()) {
			const data = settingsDoc.data();
			return {
				sensorName: data.sensorName,
				thresholds: data.thresholds,
				notificationsEnabled: data.notificationsEnabled,
				group: data.group || null,
			};
		}
		return null;
	} catch (error) {
		console.error(
			`[bioFlowSensorService][getBioFlowSensorSettings] Error getting sensor settings:`,
			error
		);
		throw error;
	}
};

/**
 * Update BioFlow sensor settings in user's sensor document
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @param {string} sensorName - Sensor name
 * @param {Object} thresholds - Threshold settings for warnings
 * @param {boolean} notificationsEnabled - Whether notifications are enabled
 * @returns {Promise<void>}
 */
export const updateBioFlowSensorSettings = async (
	userId,
	deviceId,
	sensorName,
	thresholds,
	notificationsEnabled = true
) => {
	if (!userId || !deviceId) {
		throw new Error("User ID and Device ID are required");
	}

	try {
		// Update everything in UserSiloSensors collection
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		await setDoc(
			userSensorRef,
			{
				sensorName,
				thresholds,
				notificationsEnabled,
				updatedAt: serverTimestamp(),
			},
			{ merge: true }
		);

		// Update sensor name in BioFlow collection
		const sensorRef = doc(db, BIOFLOW_COLLECTION, deviceId);
		await updateDoc(sensorRef, {
			sensorName,
			updatedAt: serverTimestamp(),
		});
	} catch (error) {
		console.error(
			`[bioFlowSensorService][updateBioFlowSensorSettings] Error updating sensor settings:`,
			error
		);
		throw error;
	}
};

/**
 * Update the group association for a BioFlow sensor
 * @param {string} userId - User ID
 * @param {string} deviceId - Device ID
 * @param {string|null} group - Group name (null to disconnect)
 * @returns {Promise<void>}
 */
export const updateBioFlowSensorGroup = async (userId, deviceId, group) => {
	if (!userId || !deviceId) {
		throw new Error("User ID and Device ID are required");
	}

	try {
		const userSensorRef = doc(
			db,
			USER_SENSORS_COLLECTION,
			`${userId}_${deviceId}`
		);
		await updateDoc(userSensorRef, {
			group: group || null,
			updatedAt: serverTimestamp(),
		});
	} catch (error) {
		console.error(
			`[bioFlowSensorService][updateBioFlowSensorGroup] Error updating sensor group:`,
			error
		);
		throw error;
	}
};

/**
 * Get all BioFlow sensors for a specific group
 * @param {string} userId - User ID
 * @param {string} group - Group name
 * @returns {Promise<Array>} Array of sensor objects
 */
export const getBioFlowSensorsByGroup = async (userId, group) => {
	return getUserBioFlowSensors(userId, group);
};

/**
 * Get daily and 7-day average consumption statistics
 * @param {string} deviceId - Device ID
 * @returns {Promise<Object>} { dailyUsage, sevenDayAverage }
 */
export const getBioFlowSensorConsumptionStats = async (deviceId) => {
	if (!deviceId) return { dailyUsage: 0, sevenDayAverage: 0 };

	const now = new Date();
	const todayStart = new Date(now);
	todayStart.setHours(0, 0, 0, 0);

	const sevenDaysAgoStart = new Date(todayStart);
	sevenDaysAgoStart.setDate(sevenDaysAgoStart.getDate() - 7);

	try {
		// Fetch raw data from 7 days ago until now
		const data = await getBioFlowSensorChartData(
			deviceId,
			sevenDaysAgoStart,
			now
		);

		if (!data || data.length === 0) {
			return { dailyUsage: 0, sevenDayAverage: 0 };
		}

		// Helper to calculate consumption from a list of points
		const calculateConsumption = (points) => {
			if (!points || points.length < 2) return 0;

			let sum = 0;
			// Sort just in case
			points.sort((a, b) => a.timestamp - b.timestamp);

			for (let i = 1; i < points.length; i++) {
				const prev = points[i - 1].totalVolume;
				const curr = points[i].totalVolume;

				if (curr >= prev) {
					sum += (curr - prev);
				} else {
					// Reset detected, assume it started from 0
					sum += curr;
				}
			}
			return sum;
		};

		// Split data
		const todayPoints = data.filter((d) => d.timestamp >= todayStart);
		const last7DaysPoints = data.filter((d) => d.timestamp < todayStart);

		const dailyUsage = calculateConsumption(todayPoints);

		// For 7-day average, we calculate total consumption over the 7 days and divide by 7
		const last7DaysConsumption = calculateConsumption(last7DaysPoints);
		const sevenDayAverage = last7DaysConsumption / 7;

		return {
			dailyUsage,
			sevenDayAverage,
		};
	} catch (error) {
		console.error(
			`[bioFlowSensorService][getBioFlowSensorConsumptionStats] Error:`,
			error
		);
		return { dailyUsage: 0, sevenDayAverage: 0 };
	}
};
